/**
 * WHAT IS HAPPENING, AND WHAT THE PLAYER IS SUPPOSED TO DO. One owner.
 *
 * 🚨 **THE MULLIGAN SHIPPED WITH NO EXPLANATION OF ITSELF.** Alex, 2026-08-14:
 *
 * > *"if the card mulligan feature is going to be on this page and not a dedicated page like the
 * > original game then there needs to be an instruction about what is going on, what stage the game
 * > is at, what the player is supposed to do."*
 *
 * The original gave the mulligan **its own screen**, and a screen is self-explanatory: nothing else
 * is on it. Folding it onto the board removed that context and nothing replaced it - the player saw
 * their hand, a DONE button, and a status line reading "OPPONENT'S TURN", which was not merely
 * unhelpful but **wrong**: it is nobody's turn during a mulligan.
 *
 * ⭐ **THE RULE THIS ENCODES: A PLAYER MUST NEVER HAVE TO GUESS WHOSE MOVE IT IS OR WHAT THE MOVE
 * IS.** Every reachable phase returns a STAGE (where we are) and an ACTION (what to do about it),
 * and `tools/verify_web_guidance.py` fails the build on any phase that returns neither - so a phase
 * added later cannot ship silent.
 *
 * ⚠️ **THE ACTION IS PHRASED AS AN IMPERATIVE, AND NAMES THE CONTROL.** "Choose cards to replace,
 * then press DONE" is actionable; "Mulligan phase" is a label pretending to be help. Where a choice
 * is irreversible the copy says so, because that is the fact a player most regrets not knowing.
 */
import type { MatchModel } from "./state";

export interface Guidance {
  /** Where we are, in two or three words. Shown large. */
  stage: string;
  /** What to do about it, as an instruction. Shown small, always present. */
  action: string;
  /** True when we are waiting on someone else - the UI greys the action rather than inviting it. */
  waiting: boolean;
}

/**
 * The ending, in words a player recognises.
 *
 * ⚠️ **THE REASON IS THE ENGINE'S ENUM AND MUST NOT BE PRINTED RAW.** `Surrender`, `Afk`,
 * `Disconnected`, `Technical` are identifiers; a player reading "Technical" learns nothing about
 * whether the match counted.
 */
function endingWords(reason: string, won: boolean): string {
  switch (reason) {
    case "Surrender":
      return won ? "Your opponent conceded." : "You conceded.";
    case "Afk":
      return won ? "Your opponent ran out of time." : "You ran out of time.";
    case "Disconnected":
      return won ? "Your opponent lost connection." : "You lost connection.";
    case "Technical":
    case "MatchFailedToStart":
      return "The match ended early, and nothing was scored against you.";
    default:
      return won ? "The last blow was yours." : "Your hero has fallen.";
  }
}

export function guidance(model: MatchModel, replacing: number): Guidance {
  const phase = model.timer.State ?? "";

  if (model.players.length === 0 || phase === "" || phase === "NotStarted") {
    return { stage: "DEALING", action: "Shuffling both decks and dealing the opening hands.", waiting: true };
  }

  if (phase === "Mulligan") {
    // ⚠️ `IsFinishedMulligan` is the SERVER's flag, not a local one. A locally-tracked "I pressed
    // DONE" would disagree with the server the moment a command is refused or retried.
    if (!model.self()?.IsFinishedMulligan) {
      return {
        stage: "OPENING HAND",
        action: replacing > 0
          ? `Press DONE to replace ${replacing} card${replacing === 1 ? "" : "s"}. You cannot change this later.`
          : "Tap any cards you would rather not keep, then press DONE. This is your only chance to swap them.",
        waiting: false,
      };
    }
    return { stage: "OPENING HAND", action: "Waiting for your opponent to choose their cards.", waiting: true };
  }

  // ⭐ **THE RESULT COMES FROM THE ENGINE'S `EndGame`, NOT FROM HERO HP.** HP is right only for a
  // lethal finish; a concession, an AFK timeout or a disconnect all end a match with both heroes
  // standing, and reading HP would call those wins.
  if (model.ended) {
    const won = model.ended.winnerId === model.selfId;
    return {
      stage: won ? "VICTORY" : "DEFEAT",
      action: `${endingWords(model.ended.reason, won)} Press LEAVE to return to the menu.`,
      waiting: false,
    };
  }

  if (phase === "Ended") {
    // ⚠️ **AN END STATE STILL NEEDS AN ACTION, AND THIS ONE CAUGHT A DEAD END.** The guidance gate
    // rejected "The match has finished." for having no verb, which was the right call for the wrong
    // reason: the real problem was that the board offered no way OUT of a finished match. Alex set
    // "no dead ends" as a release condition on day one.
    return {
      stage: "MATCH OVER",
      action: "Press LEAVE to return to the menu.",
      waiting: false,
    };
  }

  if (model.ourTurn()) {
    /* 🚨 **"WHY CAN'T I PLAY?" - ALEX ASKED IT THREE TIMES IN ONE SESSION**, and each time the board
     * was working correctly and the instruction line was telling him to do something impossible.
     *
     * *"i put this card on the table why cant i play?"* - he had 0/5 Foxfire, having just spent all
     * five. *"i am trying to play the wolf why cant i play it"* - same. The rail said **"Drag a card
     * from your hand onto a slot to play it"** the entire time.
     *
     * ⛔ **A CONSTANT INSTRUCTION IS NOT GUIDANCE, IT IS WALLPAPER.** It described the general shape
     * of a turn and never the state the player was actually in, so at the exact moment they needed
     * to be told "you have nothing left to spend" it confidently told them to spend. Then the cards
     * are disabled and nothing happens on click, which reads as the game being broken.
     *
     * ⭐ **THE CHEAPEST CARD IN HAND ANSWERS IT.** If the player cannot afford even that, there is
     * no play available and the only move is END TURN - so say so, and say how short they are. */
    const pool = model.self()?.Mana;
    const have = typeof pool?.Current === "number" ? pool.Current : null;
    const hand = model.zone(model.selfId, "InHand");
    const costs = hand
      .map((c) => (typeof c.Mana?.Current === "number" ? c.Mana.Current : null))
      .filter((n): n is number => n !== null);
    const cheapest = costs.length ? Math.min(...costs) : null;

    if (have !== null && cheapest !== null && have < cheapest) {
      return {
        stage: "YOUR TURN",
        action: hand.length === 0
          ? "No cards in hand. Attack with a creature if you can, then press END TURN."
          : `No Foxfire left - your cheapest card costs ${cheapest} and you have ${have}. `
            + "Attack with a creature if you can, or burn a card, then press END TURN.",
        waiting: false,
      };
    }

    return {
      stage: "YOUR TURN",
      action: "Drag a card from your hand onto a slot to play it, tap a creature then a target to attack, then press END TURN.",
      waiting: false,
    };
  }

  return { stage: "OPPONENT'S TURN", action: "Waiting for your opponent to move.", waiting: true };
}
